import { graphql } from "@acto/gql";
import type { FilterQueryInput, TestWorkflowQueryQuery } from "@acto/gql/graphql";
import { useCallback, useState } from "react";
import { useClient } from "urql";

const TestWorkflowQueryDocument = graphql(`
  query TestWorkflowQuery($query: FilterQueryInput!) {
    testWorkflowQuery(query: $query) {
      sampleClients {
        id
        name
      }
      totalCount
    }
  }
`);

type TestResult = TestWorkflowQueryQuery["testWorkflowQuery"];

export function useTestWorkflowQuery() {
  const client = useClient();
  const [result, setResult] = useState<TestResult | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const testQuery = useCallback(
    async (query: FilterQueryInput) => {
      setIsPending(true);
      setError(null);

      try {
        const response = await client
          .query(TestWorkflowQueryDocument, { query }, { requestPolicy: "network-only" })
          .toPromise();

        if (response.error) {
          setError(response.error);
          console.error("unable to test workflow query", response.error);

          return null;
        }

        const data = response.data?.testWorkflowQuery ?? null;
        if (data) {
          setResult(data);
          return data;
        }

        return null;
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error("Failed to test workflow query");

        setError(error);
        console.error("unable to test workflow query", error);

        return null;
      } finally {
        setIsPending(false);
      }
    },
    [client],
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { testQuery, result, isPending, error, reset };
}
